import React, { useMemo } from "react";
import Icon from "components/AppIcon";
import Input from "components/ui/Input";

function FeeCalculationCard({ label, value, description, highlight }) {
  return (
    <div
      className="p-4 rounded-lg border"
      style={{
        borderColor: highlight ? "var(--color-primary)" : "var(--color-border)",
        background: highlight ? "rgba(59,76,76,0.06)" : "var(--color-muted)",
      }}
    >
      <p className="font-caption text-xs text-muted-foreground mb-1">{label}</p>
      <p className="font-data text-lg font-semibold text-foreground">{value}</p>
      {description && (
        <p className="font-caption text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </div>
  );
}

export default function StepFees({ data, onChange, errors }) {
  const targetSize = parseFloat(data?.targetSize) || 50000000;
  const mgmtFee = parseFloat(data?.managementFee) || 0;
  const carry = parseFloat(data?.carry) || 0;
  const platformFee = parseFloat(data?.platformFee) || 0;
  const hurdleRate = parseFloat(data?.hurdleRate) || 0;

  const calculations = useMemo(() => {
    const annualMgmt = (targetSize * mgmtFee) / 100;
    const totalMgmt10yr = annualMgmt * 10;
    const platformFeeAmt = (targetSize * platformFee) / 100;
    return { annualMgmt, totalMgmt10yr, platformFeeAmt };
  }, [targetSize, mgmtFee, platformFee]);

  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      <div>
        <h3 className="font-heading text-lg lg:text-xl font-semibold text-foreground mb-1">
          Fee Structure
        </h3>
        <p className="font-caption text-sm text-muted-foreground">
          Define management fees, carried interest, and platform charges. All fees are subject to FCA disclosure requirements.
        </p>
      </div>
      {/* Fee Calculations Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FeeCalculationCard
          label="Annual Mgmt Fee"
          value={`£${calculations?.annualMgmt?.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`}
          description={`${mgmtFee}% of AUM`}
        />
        <FeeCalculationCard
          label="10-Year Mgmt Fees"
          value={`£${calculations?.totalMgmt10yr?.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`}
          description="Estimated total"
        />
        <FeeCalculationCard
          label="Platform Fee (One-off)"
          value={`£${calculations?.platformFeeAmt?.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`}
          description={`${platformFee}% of fund size`}
          highlight
        />
        <FeeCalculationCard
          label="Carried Interest"
          value={`${carry}%`}
          description={`Above ${hurdleRate}% hurdle`}
        />
      </div>
      {/* Management Fee */}
      <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Percent" size={18} color="var(--color-primary)" />
          <h4 className="font-caption font-semibold text-foreground">Management Fee</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Annual Management Fee (%)"
            type="number"
            placeholder="e.g. 1.5"
            value={data?.managementFee || ""}
            onChange={(e) => handleChange("managementFee", e?.target?.value)}
            required
            error={errors?.managementFee}
            description="Percentage of committed capital per annum."
          />
          <div>
            <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
              Fee Basis
            </label>
            <select
              value={data?.feeBasis || "committed"}
              onChange={(e) => handleChange("feeBasis", e?.target?.value)}
              className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              style={{ borderColor: "var(--color-border)" }}
            >
              <option value="committed">Committed Capital</option>
              <option value="invested">Invested Capital</option>
              <option value="nav">Net Asset Value</option>
            </select>
          </div>
          <div>
            <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
              Fee Period
            </label>
            <select
              value={data?.feePeriod || "investment-period"}
              onChange={(e) => handleChange("feePeriod", e?.target?.value)}
              className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              style={{ borderColor: "var(--color-border)" }}
            >
              <option value="investment-period">Investment Period Only</option>
              <option value="full-term">Full Fund Term</option>
              <option value="step-down">Step-Down After Investment Period</option>
            </select>
          </div>
        </div>
        {data?.feePeriod === "step-down" && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Step-Down Fee (%)"
              type="number"
              placeholder="e.g. 0.75"
              value={data?.stepDownFee || ""}
              onChange={(e) => handleChange("stepDownFee", e?.target?.value)}
              description="Reduced fee rate after investment period ends."
            />
          </div>
        )}
      </div>
      {/* Carried Interest */}
      <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
          <h4 className="font-caption font-semibold text-foreground">Carried Interest</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            label="Carry (%)"
            type="number"
            placeholder="e.g. 20"
            value={data?.carry || ""}
            onChange={(e) => handleChange("carry", e?.target?.value)}
            required
            error={errors?.carry}
            description="GP share of profits above hurdle."
          />
          <Input
            label="Hurdle Rate (%)"
            type="number"
            placeholder="e.g. 8"
            value={data?.hurdleRate || ""}
            onChange={(e) => handleChange("hurdleRate", e?.target?.value)}
            description="Preferred return before carry accrues."
          />
          <div>
            <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
              Carry Structure
            </label>
            <select
              value={data?.carryStructure || "whole-fund"}
              onChange={(e) => handleChange("carryStructure", e?.target?.value)}
              className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              style={{ borderColor: "var(--color-border)" }}
            >
              <option value="whole-fund">Whole Fund</option>
              <option value="deal-by-deal">Deal-by-Deal</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div>
            <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
              Catch-Up Provision
            </label>
            <select
              value={data?.catchUp || "full"}
              onChange={(e) => handleChange("catchUp", e?.target?.value)}
              className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              style={{ borderColor: "var(--color-border)" }}
            >
              <option value="full">Full Catch-Up</option>
              <option value="partial">Partial Catch-Up (50%)</option>
              <option value="none">No Catch-Up</option>
            </select>
          </div>
        </div>
      </div>
      {/* Platform & Other Fees */}
      <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Landmark" size={18} color="var(--color-primary)" />
          <h4 className="font-caption font-semibold text-foreground">Platform &amp; Other Fees</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Platform Fee (%)"
            type="number"
            placeholder="e.g. 0.5"
            value={data?.platformFee || ""}
            onChange={(e) => handleChange("platformFee", e?.target?.value)}
            description="SeedDrive platform fee on fund size (one-off)."
          />
          <Input
            label="Transaction Fee (£)"
            type="number"
            placeholder="e.g. 50000"
            value={data?.transactionFee || ""}
            onChange={(e) => handleChange("transactionFee", e?.target?.value)}
            description="One-off deal execution fee."
          />
          <Input
            label="Annual Administration Fee (£)"
            type="number"
            placeholder="e.g. 25000"
            value={data?.adminFee || ""}
            onChange={(e) => handleChange("adminFee", e?.target?.value)}
            description="Fund administration and reporting costs."
          />
        </div>
      </div>
      {/* Disclosure Notice */}
      <div
        className="flex items-start gap-3 p-4 rounded-lg"
        style={{ background: "rgba(59,76,76,0.05)", border: "1px solid rgba(59,76,76,0.15)" }}
      >
        <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <p className="font-caption text-xs text-muted-foreground leading-relaxed">
          All fees must be disclosed in the Private Placement Memorandum (PPM) and investor communications in accordance with FCA COBS 4.5 and AIFMD Annex IV reporting requirements. Platform fees are subject to VAT where applicable.
        </p>
      </div>
    </div>
  );
}